import authUserService from '@/services/auth-user.service';
import { deleteCookie } from '@/utils/cookies';
import router from '@/router';

const state = {
    user: null,
    token: null,
    authError: null,
    isLoading: false
};

const getters = {
  currentUser: state => state.user,
  isAuthenticated: state => !!state.user,
  authError: state => state.authError,
  isLoading: state => state.isLoading,

  getUserId: state => state.user?.ScioUserId || null,

  // Legacy (keep for now so we don’t break existing code)
  getUserRole: state => state.user?.role || null,

  // RBAC (new)
  getGlobalRoleCode: state => state.user?.globalRoleCode || null,
  isScioAdmin: state => state.user?.globalRoleCode === 'ADMIN',
};

function standardiseMe(me) {
  if (!me) return null;

  const rawId = me.scioUserId ?? me.userId ?? null;

  return {
    name: me.email,
    email: me.email,

    // Legacy (keep)
    role: me.role ?? null,

    // RBAC (authoritative)
    globalRoleCode: me.globalRole ?? me.globalRoleCode ?? null,

    ScioUserId: rawId != null ? parseInt(rawId, 10) : null,
  };
}

const actions = {
    async initAuth({ commit }) {
        try {
        commit('SET_LOADING', true);
        const me = await authUserService.initFromCookie();

        if (me) {
            const standardizedUser = standardiseMe(me);
            commit('SET_USER', standardizedUser);
            commit('SET_TOKEN', null);
        } else {
            commit('CLEAR_USER');
        }
        } catch (error) {
        console.error('Error initializing auth:', error);
        commit('SET_AUTH_ERROR', 'Failed to initialize authentication');
        } finally {
        commit('SET_LOADING', false);
        }
    },

    async logout({ commit }, { skipServer = false, redirect = true } = {}) {
        // Clear authentication data
        commit('SET_USER', null);
        commit('SET_TOKEN', null);
        try {
            await authUserService.logout({ skipServer });
        } catch {
            // Continue clearing local state if the server session already expired.
        }
        // Clear UI persistence cookies/local values if any remain
        try {
            deleteCookie('lastPath');
        } catch {
            // Cookie may already be unavailable or expired.
        }
        try {
            deleteCookie('tabPaths');
        } catch {
            // Cookie may already be unavailable or expired.
        }
        try {
            localStorage.removeItem('activeTabs');
        } catch {
            // Storage can be unavailable in hardened browser contexts.
        }
        
        // Redirect to login page
        if (redirect && router.currentRoute.value.name !== 'login') {
          router.push({ name: 'home' });
        }
      }
    ,
    
    setUser({ commit }, user) {
        // Accept user object from SSO (MSAL) or manual login
        commit('SET_USER', user);
        // No token in localStorage anymore; cookie is used
        commit('SET_TOKEN', null);
    },
    
    clearAuthError({ commit }) {
        commit('CLEAR_AUTH_ERROR');
    }
};

const mutations = {
    SET_USER(state, user) {
        state.user = user;
    },
    
    SET_TOKEN(state, token) {
        state.token = token;
    },
    
    CLEAR_USER(state) {
        state.user = null;
        state.token = null;
    },
    
    SET_AUTH_ERROR(state, error) {
        state.authError = error;
    },
    
    CLEAR_AUTH_ERROR(state) {
        state.authError = null;
    },
    
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
